import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { AvatarView } from '../../components/ui/AvatarView';
import { searchChannels, fetchChannel } from '../../api/dataApi';
import { useStore } from '../../store/StoreContext';
import { queryClient, queryKeys } from '../../lib/queryClient';
import type { Channel } from '../../types';
import { usePersistedState, oneOf } from '../../lib/usePersistedState';

// Default cap, and its default framing copy, for the Multi-Channel
// Report flow - kept in sync by hand with backend/internal/report/
// access.go's MaxAdditionalChannels (both cap the same thing there), so a
// report-flow caller sees the limit here rather than discovering it only
// after the server silently drops the rest. A caller comparing channels
// on a chart instead (no backend involved, purely client-side rendering)
// passes its own `maxAdditional`/`noun` via location.state - see
// ChartScreen.tsx.
const DEFAULT_MAX_ADDITIONAL_CHANNELS = 4;

interface PickerLocationState {
  // Full channel objects, not bare ids: there's no "fetch channel by id"
  // endpoint (only fetchChannel(nickname)), so the caller can't
  // re-hydrate a chip list from ids alone after this screen returns -
  // passing the objects both ways avoids that round trip entirely.
  selected?: Channel[];
  // The channel already being reported on/charted (Channel.id, the
  // identifier enqueueReportJob/sendMessage/etc. use throughout this
  // codebase - Channel.nickname is a separate, display-only handle).
  // Excluded from both tabs so you can't "add" the primary channel to
  // its own report/chart.
  excludeId?: string;
  returnTo?: string;
  // Screen title, cap, and cap-reached copy - all optional so existing
  // report-flow callers (ReportsScreen.tsx) need no changes.
  title?: string;
  maxAdditional?: number;
  // What the selection is *for*, used only in the "Up to N additional
  // ___s" cap notice, e.g. "report" or "chart".
  noun?: string;
}

function ResultRow({ channel, checked, disabled, onToggle }: { channel: Channel; checked: boolean; disabled: boolean; onToggle: () => void }) {
  return (
    <button onClick={onToggle} disabled={disabled && !checked} className="w-full text-left disabled:opacity-40">
      <AppCard className="flex items-center gap-3">
        <AvatarView symbol={channel.avatarSymbol} colorHex={channel.accentHex} imageKey={channel.avatarImageKey} size={44} />
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="font-medium truncate">{channel.title}</span>
          <span className="text-xs text-black/40 dark:text-white/40 truncate">{channel.nickname}</span>
        </div>
        <span
          className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
            checked ? 'bg-blue-600 border-blue-600' : 'border-black/20 dark:border-white/20'
          }`}
        >
          {checked && <Check size={14} color="white" strokeWidth={3} />}
        </span>
      </AppCard>
    </button>
  );
}

// Picks up to `maxAdditional` channels (own or public) to fold in
// alongside the channel already being reported on or charted
// (excludeId) - shared by both the Multi-Channel Report flow
// (ReportsScreen.tsx) and the multi-channel chart-comparison flow
// (ChartScreen.tsx), distinguished only by the title/cap/noun passed via
// location.state. Mirrors TemplatePickerScreen.tsx's
// "mine vs public, debounced search, return via navigate state" shape,
// with one deliberate difference: the "mine" tab searches store.channels
// in memory instead of hitting a server search endpoint. Unlike
// templates (which can number 100k+ and have their own search_mine
// route), a single account's own channel list already lives fully
// loaded in the store (see StoreContext's `channels`), so there's
// nothing to gain from a round trip for it - see the explore notes on
// this feature for why no channel/search_mine.wsl was added.
export function ChannelPickerScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { channels } = useStore();
  const state = (location.state ?? {}) as PickerLocationState;
  const returnTo = state.returnTo ?? '/channels';
  const excludeId = state.excludeId;
  const title = state.title ?? 'Add channels to this report';
  const maxAdditional = state.maxAdditional ?? DEFAULT_MAX_ADDITIONAL_CHANNELS;
  const noun = state.noun ?? 'report';

  const [tab, setTab] = usePersistedState<'mine' | 'public'>('picker.channels.tab', 'mine', oneOf(['mine', 'public'] as const));
  const [query, setQuery] = useState('');
  const [publicResults, setPublicResults] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, Channel>>(
    () => new Map((state.selected ?? []).map((c) => [c.id, c]))
  );

  const requestId = useRef(0);

  useEffect(() => {
    if (tab !== 'public') return;
    const thisRequest = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const page = await queryClient.query({
          queryKey: queryKeys.channelSearch(query, 0),
          queryFn: () => searchChannels(query, 0, 50),
        });
        const hydrated = (await Promise.all(page.data.map((n) => fetchChannel(n)))).filter(
          (c): c is Channel => c !== null
        );
        if (requestId.current === thisRequest) setPublicResults(hydrated);
      } catch {
        if (requestId.current === thisRequest) setPublicResults([]);
      } finally {
        if (requestId.current === thisRequest) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tab, query]);

  const mineResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    return channels.filter((c) => {
      if (c.id === excludeId) return false;
      if (!q) return true;
      return c.title.toLowerCase().includes(q) || c.nickname.toLowerCase().includes(q);
    });
  }, [channels, query, excludeId]);

  const results = tab === 'mine' ? mineResults : publicResults.filter((c) => c.id !== excludeId);
  const atCap = selected.size >= maxAdditional;

  const toggle = (channel: Channel) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(channel.id)) {
        next.delete(channel.id);
      } else if (next.size < maxAdditional) {
        next.set(channel.id, channel);
      }
      return next;
    });
  };

  const done = () => {
    navigate(returnTo, { state: { selectedChannels: Array.from(selected.values()) }, replace: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title={title} onBack={done} />

      <SegmentedControl
        options={[
          { value: 'mine' as const, label: 'My own' },
          { value: 'public' as const, label: 'Public' },
        ]}
        value={tab}
        onChange={setTab}
      />

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={tab === 'mine' ? 'Search my channels' : 'Search public channels'}
        className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
      />

      {atCap && (
        <span className="text-xs text-black/40 dark:text-white/40">
          Up to {maxAdditional} additional channels per {noun} - deselect one to add another.
        </span>
      )}

      <div className="flex flex-col gap-2.5 pb-20">
        {tab === 'public' && loading && (
          <span className="text-sm text-black/40 dark:text-white/40 py-4 text-center">Searching...</span>
        )}
        {!(tab === 'public' && loading) && results.length === 0 && (
          <span className="text-sm text-black/40 dark:text-white/40 py-4 text-center">
            {tab === 'mine'
              ? query
                ? 'No channels of yours match that search.'
                : "You don't have any other channels yet."
              : query
                ? 'No public channels match that search.'
                : 'No public channels yet.'}
          </span>
        )}
        {!(tab === 'public' && loading) &&
          results.map((c) => (
            <ResultRow
              key={c.id}
              channel={c}
              checked={selected.has(c.id)}
              disabled={atCap}
              onToggle={() => toggle(c)}
            />
          ))}
      </div>

      {/* z-40: above the site-wide AppFooter's z-30 (App.tsx) - both are
          `fixed bottom-0`, and without this the footer's translucent
          backdrop-blur background painted over this bar, washing out the
          Done button underneath it to near-invisibility. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-white dark:bg-[#1c1c1e] border-t border-black/10 dark:border-white/10">
        <Button variant="primary" full onClick={done}>
          Done{selected.size > 0 ? ` (${selected.size} selected)` : ''}
        </Button>
      </div>
    </div>
  );
}
